import axios, { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { showToast } from '../../components/Toast/Toast';
import { ToastTypes } from '../../components/Toast/Toast.types';

const apiClient = axios.create({
  // In development, use proxy (no baseURL); in production, use direct API URL
  baseURL: process.env.NODE_ENV === 'production' ? process.env.REACT_APP_OPSUI_API_URL : '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Global variable to track if a refresh is in progress
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

export const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  if (apiClient.defaults.headers.common['Authorization'] === undefined) {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }
  }

  return config;
};

export const errorResponseInterceptor = async (error: any) => {
  const originalRequest = error.config;
  const localAuthDisabled = process.env.REACT_APP_DISABLE_AUTH_LOCAL;

  // Prevent infinite retry loops - if request was already retried, don't try again
  if (originalRequest?._retry) {
    return Promise.reject(error);
  }

  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    // handle API gateway timeout
    showToast({
      toastType: ToastTypes.Error,
      label: 'The request is taking longer than usual to process.',
      subtext: 'Please check back later to see if your changes have been applied.',
      id: 'apiGatewayTimeout',
    });
    throw new Error('apiGatewayTimeout');
  } else if (error.response?.status === 401 && !originalRequest._retry && originalRequest.url !== '/refresh-token') {
    /* istanbul ignore next */
    if (localAuthDisabled === 'true') return;

    // Don't attempt token refresh if we're already on the login page
    if (window.location.pathname === '/login') {
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return apiClient.request(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refreshToken');

    // If no refresh token, go directly to login
    if (!refreshToken) {
      processQueue(error, null);
      isRefreshing = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.replace(`${window.location.origin}/login`);
      return Promise.reject(error);
    }

    try {
      const refreshResponse = await apiClient.post('/refresh-token', refreshToken, {
        transformRequest: (data, headers) => {
          delete (headers as any)['Authorization'];
          delete (headers as any).common?.['Authorization'];
          return data;
        },
      });
      const newAuthToken = refreshResponse.data;
      localStorage.setItem('accessToken', newAuthToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newAuthToken}`;
      
      // Process the queue with the new token
      processQueue(null, newAuthToken);
      isRefreshing = false;
      
      const retryRequest = {
        ...originalRequest,
        headers: {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAuthToken}`,
        },
      };
      return apiClient.request(retryRequest);
    } catch (refreshError) {
      // If refresh fails, clear tokens and redirect to login
      processQueue(refreshError, null);
      isRefreshing = false;
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.replace(`${window.location.origin}/login`);
      return Promise.reject(error);
    }
    // if we receive a 401 after refreshing the token or receive any error when trying to refresh the token we should be logged out
  } else if (error.response?.status === 401 || originalRequest?.url === '/refresh-token') {
    /* istanbul ignore next */
    if (localAuthDisabled === 'true') return;
    // clear tokens from storage as neither are valid
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.replace(`${window.location.origin}/login`);
    return Promise.reject(error);
  } else {
    // For non-401 errors, don't retry - just reject with the original error
    return Promise.reject(error);
  }
};

apiClient.interceptors.request.use(requestInterceptor);
apiClient.interceptors.response.use((response: AxiosResponse) => response, errorResponseInterceptor);

export default apiClient;
